/** The COMPUTED-VALUES channel of one (island, lane) — wire contract §4 and
 *  §3.3, vc-impl's routes. The state never carries computed values; this
 *  channel does, with the one sync rule of §4a:
 *
 *    open    → GET the BASE the open names ({as_of, items}), apply it, then
 *              catch up from its `as_of`;
 *    catchUp → GET `since?cursor=` ({cursor, items, tombstones}): the latest
 *              of each changed item, and a tombstone for each item that left
 *              the lane's view; a 410 (`cursor_too_old`: older than the
 *              tombstone horizon) re-opens from the base;
 *    frame   → a live derived frame's items.
 *
 *  Every item is `{id, _label, _v, ...derived fields}`, and the NEWER `_v`
 *  wins everywhere (a re-sent item carries `_v` = the drop time, vc-impl
 *  2026-10-07), so base, since and frames need no ordering between them.
 *  The store side is the sync client's `applyComputed` / `dropComputed`. */

import { sessionEnded } from "../island-store/island-store";

export type ComputedItem = { id: string; _label?: string; _v?: number | null; [field: string]: unknown };

export type ComputedSink = {
  applyComputed(subscriptionId: string, items: ComputedItem[]): void;
  dropComputed(ids: string[]): void;
};

export type ComputedChannelOptions = {
  subscriptionId: string;
  /** the base URL the open named (`computed.base`), relative to `apiBase` */
  baseUrl: string;
  /** builds the since URL for a cursor (`computed.since` + `&cursor=`) */
  sinceUrl: (cursor: number) => string;
  apiBase: string;
  sink: ComputedSink;
  fetch?: typeof fetch;
  /** these URLs are no longer good — the reader's access changed since the
   *  open handed them out (a 410 `access_changed`), or what they name is no
   *  longer served (a 410 `field_set_changed`, e.g. a claims class's stale
   *  default changed in a deploy): the island has to be opened afresh for
   *  new ones — the caller's re-open */
  onAccessChanged?: () => void;
};

/** Thrown when this channel's URLs went stale with the reader's access. */
export class AccessChanged extends Error {
  constructor() {
    super("access changed — re-open");
    this.name = "AccessChanged";
  }
}

const accessChanged = (status: number, body: any) =>
  status === 410 && ["access_changed", "field_set_changed"].includes(body?.code ?? body?.detail?.code);

export class ComputedChannel {
  /** the newest `_v` this channel has applied per item: what a re-sent or
   *  late answer is compared with before it reaches the store */
  protected versions = new Map<string, number>();
  cursor: number | null = null;
  /** the base the open names is applied (and its first catch-up ran): until
   *  then the channel is still LOADING — "quiet" must wait for it */
  baseApplied = false;
  /** replaced by a channel of another lane: its answers still in flight
   *  are not applied over the new lane's */
  protected retired = false;

  constructor(protected readonly opts: ComputedChannelOptions) {}

  private async get(path: string): Promise<{ status: number; body: any }> {
    // called UNBOUND: the window's fetch called as a method of this channel
    // (`this.f(...)`) is refused by a browser — "Illegal invocation" — while
    // Node allows it, so a page loaded no computed values or claims at all
    // while every test passed (VC-e2e, 2026-10-13)
    const f = this.opts.fetch ?? fetch;
    const r = await f(`${this.opts.apiBase}${path}`, { credentials: "include" });
    // the session ended on the server: as a sign-out (VC-Authz, 2026-10-19)
    if (r.status === 401) sessionEnded();
    const body = r.status === 204 ? null : await r.json().catch(() => null);
    return { status: r.status, body };
  }

  /** Items not older than what this channel applied (a null `_v` — a
   *  default never written — is the oldest of all). An EQUAL `_v` is the
   *  same write and passes: a live frame carries part of an item's fields,
   *  the since answer the whole item with the same `_v`. */
  protected newer(items: ComputedItem[]): ComputedItem[] {
    const out: ComputedItem[] = [];
    for (const it of items) {
      const v = typeof it._v === "number" ? it._v : -Infinity;
      const held = this.versions.get(it.id);
      if (held !== undefined && held > v) continue;
      this.versions.set(it.id, v);
      out.push(it);
    }
    return out;
  }

  /** Stop for good: a re-open answered another lane and a new channel took
   *  over (32-b3f). Answers already in flight are dropped. */
  retire(): void {
    this.retired = true;
  }

  /** the ids this channel holds a value for */
  heldIds(): string[] {
    return Array.from(this.versions.keys());
  }

  holds(id: string): boolean {
    return this.versions.has(id);
  }

  protected apply(items: ComputedItem[]): void {
    if (this.retired) return;
    const fresh = this.newer(items);
    if (fresh.length) this.opts.sink.applyComputed(this.opts.subscriptionId, fresh);
  }

  /** `attempt`: how many times in a row the base was reloaded because the
   *  since after it was refused (410) */
  async open(attempt = 0): Promise<void> {
    if (this.retired) return;
    const { status, body } = await this.get(this.opts.baseUrl);
    if (this.retired) return;
    if (accessChanged(status, body)) return this.staleAccess();
    if (status !== 200 || !body) throw new Error(`computed base failed (${status})`);
    this.apply(body.items ?? []);
    this.cursor = typeof body.as_of === "number" ? body.as_of : null;
    await this.catchUp({ attempt });
    if (!this.retired) this.baseApplied = true;
  }

  async catchUp(opts: { attempt?: number } = {}): Promise<void> {
    if (this.retired || this.cursor === null) return;
    const { status, body } = await this.get(this.opts.sinceUrl(this.cursor));
    // Retirement invalidates the entire response, including tombstones and
    // access-change callbacks, not just its whole-record items.
    if (this.retired) return;
    if (accessChanged(status, body)) return this.staleAccess();
    if (status === 410) {
      // older than the tombstone horizon: start again from the base — ONCE.
      // A since refused again after a reloaded base means the base itself is
      // older than the horizon (the open's URL names an old one): reloading
      // it would loop for ever, so open the island afresh for new URLs
      if ((opts.attempt ?? 0) >= 1) return this.staleAccess();
      this.versions.clear();
      this.cursor = null;
      this.baseApplied = false;
      await this.open((opts.attempt ?? 0) + 1);
      return;
    }
    if (status !== 200 || !body) throw new Error(`computed since failed (${status})`);
    this.apply(body.items ?? []);
    const gone: string[] = [];
    for (const t of body.tombstones ?? []) {
      const held = this.versions.get(t.id);
      // a tombstone older than a value applied since does not remove it
      if (held !== undefined && typeof t.deleted_at === "number" && held > t.deleted_at) continue;
      this.versions.delete(t.id);
      gone.push(t.id);
    }
    if (gone.length) this.opts.sink.dropComputed(gone);
    if (typeof body.cursor === "number") this.cursor = Math.max(this.cursor ?? 0, body.cursor);
  }

  /** The URLs name the reader and an access epoch (Tom, 2026-10-11:
   *  cache, securely to a changing user): reloading from the same base
   *  would be refused too. Stop, and let the caller open afresh. */
  private staleAccess(): never {
    this.cursor = null;
    this.versions.clear();
    this.opts.onAccessChanged?.();
    throw new AccessChanged();
  }

  /** A live derived frame: today's `dag_patch` shape (`derived: true`,
   *  `changes.nodes[]`) or the recorded `node_changes[]` one. */
  onFrame(frame: any): void {
    const changes: any[] = frame?.changes?.nodes ?? frame?.node_changes ?? [];
    this.apply(
      changes.map((c) => ({ id: c.node_id, _label: c.node_type, ...(c.properties ?? {}) }) as ComputedItem),
    );
  }
}
